import React, {useEffect} from "react";
import {Grid, Modal} from "semantic-ui-react";
import {TicketModalProps} from "./TicketModels";
import {useTicketDetails} from "./hooks/useTicketDetails";
import {TicketTable} from "./TicketTable";
import {closeTicketModal} from "./TicketModalService";
import {
    LoadingMessage,
    ErrorMessage,
    NoDataMessage
} from "@/components/SystemMessages";
import {TicketModalHeader} from "./TicketModalHeader";
import {TicketInfo} from "./TicketInfo";
import {connect} from "react-redux";
import {AppState} from "@/state/rootReducer";
import {IBusinessUnit} from "../application/api/applicationModels";

interface TicketModalPropsWithRedux extends TicketModalProps {
    clientTreeHierarchy: IBusinessUnit[];
    conceptName: string;
}

const TicketModalComponent: React.FC<TicketModalPropsWithRedux> = ({
    isOpen,
    onClose,
    locationId,
    businessDate,
    ticketNumber,
    clientTreeHierarchy,
    conceptName
}) => {
    const handleClose = onClose || closeTicketModal;

    const {data, loading, error, errorStatus} = useTicketDetails({
        locationId,
        businessDate,
        ticketNumber
    });

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, handleClose]);

    if (!isOpen) return null;

    // Don't render if required props are missing
    if (!locationId || !businessDate || !ticketNumber) {
        return null;
    }

    return (
        <Modal
            className="modal-box"
            open={isOpen}
            onClose={handleClose}
            closeIcon
            closeOnDimmerClick={false}
        >
            <Modal.Content style={{overflow: "auto"}}>
                {loading ? (
                    <LoadingMessage />
                ) : error && errorStatus === 404 ? (
                    <NoDataMessage marginTop={25} text="No Receipt was found." />
                ) : error ? (
                    <ErrorMessage />
                ) : data ? (
                    <Grid>
                        <Grid.Row>
                            <Grid.Column>
                                {data.checkNumber && (
                                    <TicketModalHeader
                                        checkNumber={data.checkNumber}
                                    />
                                )}
                                <TicketInfo
                                    ticket={data}
                                    locationId={locationId}
                                    clientTreeHierarchy={clientTreeHierarchy}
                                    conceptName={conceptName}
                                />
                                <div className="ticket-content">
                                    <div className="ticket-info-section">
                                        <TicketTable ticket={data} />
                                    </div>
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                ) : null}
            </Modal.Content>
        </Modal>
    );
};

const mapStateToProps = (state: AppState) => ({
    clientTreeHierarchy: state.application.clientTreeHierarchy,
    conceptName: state.application.selectedClientConcept.conceptName
});

export const TicketModal = connect(mapStateToProps)(TicketModalComponent);
